import React from 'react';
import { LiveKitRoom, RoomAudioRenderer, StartAudio, TrackToggle, VideoTrack, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { VideoOff } from 'lucide-react';

// Lower-third question graphic shown over the broadcast (also used as the presenter preview).
export const QuestionOverlay = ({ overlay, color = '#2347FF', preview = false }) => {
  const size = { sm: 'text-xs sm:text-base', md: 'text-sm sm:text-xl', lg: 'text-base sm:text-2xl' }[overlay?.size || 'md'];
  return (
    <div className={`pointer-events-none absolute inset-x-2 bottom-2 sm:inset-x-6 sm:bottom-6 transition-all duration-500 ease-out ${overlay ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'}`} data-testid={preview ? 'overlay-preview' : 'question-overlay'}>
      {overlay && (
        <div className="max-w-3xl overflow-hidden rounded-lg bg-white/95 shadow-2xl ring-1 ring-black/5 backdrop-blur sm:rounded-xl">
          <div className="flex items-center gap-2 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white sm:px-4 sm:py-1.5 sm:text-[11px]" style={{ background: color }}>
            {preview ? 'Preview — not live' : overlay.question_id ? 'Question' : 'Topic'}{overlay.name ? <span className="font-semibold normal-case tracking-normal opacity-90">· from {overlay.name}</span> : null}
          </div>
          <p className={`px-3 py-2 font-bold leading-snug text-slate-900 sm:px-4 sm:py-3 ${size}`}>{overlay.text}</p>
        </div>
      )}
    </div>
  );
};

const Stage = ({ host }) => {
  const tracks = useTracks([{ source: Track.Source.ScreenShare, withPlaceholder: false }, { source: Track.Source.Camera, withPlaceholder: false }]);
  const screen = tracks.find((t) => t.source === Track.Source.ScreenShare);
  const cam = tracks.find((t) => t.source === Track.Source.Camera);
  const main = screen || cam;
  return (
    <div className="absolute inset-0">
      {main ? <VideoTrack trackRef={main} className="h-full w-full object-contain" /> : (
        <div className="grid h-full place-items-center text-center text-slate-400"><div><VideoOff className="mx-auto mb-2 h-8 w-8" /><p className="text-sm font-semibold">{host ? 'Turn on your camera or share your screen' : 'The presenter will appear here shortly'}</p></div></div>
      )}
      {screen && cam && <div className="absolute right-3 top-3 w-1/4 min-w-[120px] overflow-hidden rounded-lg ring-2 ring-white/30"><VideoTrack trackRef={cam} className="w-full" /></div>}
      <RoomAudioRenderer />
      {!host && <StartAudio label="Tap to turn on sound" className="absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-white px-4 py-2 text-xs font-bold text-slate-900 shadow" />}
      {host && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2" data-testid="host-controls">
          {[Track.Source.Camera, Track.Source.Microphone, Track.Source.ScreenShare].map((s) => (
            <TrackToggle key={s} source={s} className="rounded-full bg-slate-900/80 px-3 py-2 text-xs font-bold text-white ring-1 ring-white/20 hover:bg-slate-800 data-[lk-enabled=true]:bg-emerald-600" data-testid={`host-toggle-${s}`}>
              {{ camera: 'Camera', microphone: 'Mic', screen_share: 'Share screen' }[s]}
            </TrackToggle>
          ))}
        </div>
      )}
    </div>
  );
};

// 16:9 broadcast stage. `conn` = { token, url } from the backend, or null when video isn't available.
export const LiveStage = ({ conn, host = false, overlay, color, previewOverlay, notice }) => (
  <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-[#0B0F24] ring-1 ring-white/10" data-testid="live-stage">
    {conn ? (
      <LiveKitRoom token={conn.token} serverUrl={conn.url} connect video={false} audio={false} options={{ adaptiveStream: true, dynacast: true }}>
        <Stage host={host} />
      </LiveKitRoom>
    ) : (
      <div className="grid h-full place-items-center px-6 text-center text-sm font-semibold text-slate-400" data-testid="stage-notice">{notice}</div>
    )}
    {previewOverlay ? <QuestionOverlay overlay={previewOverlay} color={color} preview /> : <QuestionOverlay overlay={overlay} color={color} />}
  </div>
);
